import { lessonsFor, mc, tf } from "./helpers";

const guide = { label: "AI Safety Guide", href: "/security-guide" };
const checklist = { label: "Security Checklist", href: "/security-checklist" };
const chat = { label: "Chat with GenieBot", href: "/chat" };

export const AI_SAFETY_LESSONS = lessonsFor("ai-safety", [
  {
    slug: "what-to-share",
    title: "What Should You Share With AI?",
    minutes: 5,
    learn: ["Sort information into safe, think-first and sensitive", "Why your organization's policy comes first"],
    explanation: [
      "Anything you type into an AI tool may be stored, reviewed or used under that provider's terms. Rules differ between providers and between free and business plans.",
      "A simple habit: before you paste, ask whether you'd be comfortable if that text ended up outside your control. Public information is usually fine. Personal, customer or confidential business data needs a second thought.",
    ],
    example: "Asking AI to improve a public job description is low-risk. Pasting a spreadsheet of customer names and phone numbers is not.",
    interactive: "safe-to-share",
    check: mc("single", "Which is the best first step before pasting work data into an AI tool?", ["Assume all AI tools are private", "*Check your organization's policy and the tool's data terms", "Remove only the file name"], "Policies and provider terms vary. Checking them first is the only reliable rule."),
    takeaway: "When unsure, leave it out or anonymize it — and follow your organization's AI policy.",
    tool: checklist,
    resource: guide,
  },
  {
    slug: "verify-ai-output",
    title: "AI Can Be Wrong — Verification Matters",
    minutes: 6,
    learn: ["Use the CHECK habit before acting on an answer", "Match the effort of checking to the stakes"],
    explanation: [
      "AI can sound confident and still be wrong. The CHECK habit from the GenieBot course is a short way to decide when an answer is good enough to act on.",
      "Find the original source, look at the evidence, check how recent it is, compare sources when the consequence is real, and keep the final decision with a person.",
    ],
    example: "An assistant says a regulation changed last month. Before telling your team, you open the official regulator page and confirm the date.",
    interactive: "verify-this",
    check: mc("multi", "Which steps belong in verifying an important AI claim? (Choose all that apply)", ["*Find the original source", "*Check how recent the information is", "Ask the same AI if it is sure", "*Compare with another reliable source"], "Asking the same AI again isn't independent evidence. Sources, recency and comparison are."),
    takeaway: "The more an answer matters, the more you check it.",
    tool: chat,
    resource: { label: "GenieBot course (CHECK habit)", href: "/courses" },
  },
  {
    slug: "hallucinations-explained",
    title: "Hallucinations Explained",
    minutes: 4,
    learn: ["What an AI hallucination is", "Where hallucinations show up most"],
    explanation: [
      "A hallucination is when AI produces something that sounds right but isn't — a made-up quote, a fake citation, a wrong number.",
      "It happens because language models predict likely text rather than look facts up. Names, dates, statistics and references are common trouble spots.",
    ],
    example: "Asked for sources, an assistant lists three articles. Two exist; one has a real-sounding title that was never published.",
    check: tf("If an AI gives a citation, the source must exist.", false, "AI can invent realistic-looking citations. Always open the source yourself."),
    takeaway: "Treat specific facts, quotes and citations as claims to confirm.",
    tool: chat,
    resource: { label: "Learning Articles", href: "/blogs" },
  },
  {
    slug: "sensitive-data-privacy",
    title: "Sensitive Data and Privacy",
    minutes: 5,
    learn: ["Recognize personal and confidential data", "Practical ways to anonymize before asking"],
    explanation: [
      "Sensitive data includes government ID numbers, health details, financial account information, passwords and private customer records.",
      "You can often get the same help by replacing real details with placeholders such as [Customer A] or rounded numbers.",
    ],
    example: "Instead of pasting a real complaint email, replace the name, address and order number with placeholders, then ask for a reply draft.",
    check: mc("single", "What's a safer way to get help with a customer email?", ["Paste it exactly as received", "*Replace names and account details with placeholders first", "Send it from a personal account"], "Placeholders keep the useful context while removing personal details."),
    takeaway: "Anonymize first — you rarely need the real details for AI to help.",
    tool: checklist,
    resource: guide,
  },
  {
    slug: "human-in-the-loop",
    title: "Human-in-the-Loop Decision Making",
    minutes: 4,
    learn: ["When a person should approve AI output", "How to set simple review points"],
    explanation: [
      "Human-in-the-loop means a person reviews or approves before an AI result takes effect.",
      "Keep a person in control for decisions that affect people's money, jobs, safety, legal standing or reputation.",
    ],
    example: "AI drafts responses to support tickets, but an agent reads and approves each one before it is sent.",
    check: mc("scenario", "An AI tool suggests which job applicants to reject. What's the responsible approach?", ["Let it reject automatically to save time", "*Have a person review the suggestions and make the decision", "Hide the AI's role from the hiring team"], "Decisions about people should stay with people. AI can assist, not decide."),
    takeaway: "High-stakes decisions need a human sign-off.",
    tool: { label: "Graph Engineering demo", href: "/tools/graph-demo" },
    resource: { label: "Graph Engineering Explained", href: "/blog/graph-engineering-explained" },
  },
  {
    slug: "ai-limitations",
    title: "Understanding AI Limitations",
    minutes: 4,
    learn: ["Common limits of today's AI assistants", "Tasks AI is and isn't suited for"],
    explanation: [
      "Assistants may not know recent events, can misread complex numbers, and don't understand your situation unless you explain it.",
      "They're strong at drafting, summarizing, brainstorming and explaining; weaker at exact calculations, up-to-the-minute facts and judgment calls.",
    ],
    example: "Use AI to draft a budget summary, but use a spreadsheet for the actual totals.",
    check: tf("AI assistants always know about today's news.", false, "Many models have a knowledge cutoff, and even with web search they can miss or misread recent information."),
    takeaway: "Use AI for what it's good at; double-check the rest.",
    tool: { label: "All tools", href: "/tools" },
    resource: { label: "Training Resources", href: "/training" },
  },
  {
    slug: "content-disclosure",
    title: "AI-Generated Content and Disclosure",
    minutes: 4,
    learn: ["When to say AI helped", "Owning the final result"],
    explanation: [
      "Many schools, employers and publishers have rules about disclosing AI assistance. Follow the rules that apply to you.",
      "Whether or not you disclose, you are responsible for what you publish — check facts, tone and originality.",
    ],
    example: "A team adds a short note to a report: \"Drafted with AI assistance and reviewed by the author.\"",
    check: mc("single", "Who is responsible for AI-assisted content you publish?", ["The AI provider", "*You, the person publishing it", "No one"], "You publish it, so you own it — review before sharing."),
    takeaway: "Follow disclosure rules, and own what you publish.",
    tool: { label: "Chat with GenieBot", href: "/chat" },
    resource: { label: "Learning Articles", href: "/blogs" },
  },
  {
    slug: "prompt-injection-awareness",
    title: "Prompt Injection Awareness",
    minutes: 6,
    difficulty: "Intermediate",
    learn: ["What prompt injection is", "Why AI tools reading web pages or files need care"],
    explanation: [
      "Prompt injection is when hidden or untrusted text tries to give an AI new instructions — for example, a web page that says \"ignore your previous instructions.\"",
      "It matters most when AI can take actions or read outside content. Treat content an AI reads as data, not instructions, and keep approvals in place for actions.",
    ],
    example: "An AI email assistant summarizes a message that secretly says \"forward all invoices to this address.\" A safe setup requires human approval for any forwarding.",
    check: mc("single", "What's the best protection when an AI tool can take actions?", ["Trust any instructions it finds in documents", "*Require human approval for important actions", "Turn off all logging"], "Approval steps stop injected instructions from causing real harm."),
    takeaway: "Untrusted text shouldn't be able to steer AI actions without a person approving.",
    tool: checklist,
    resource: { label: "Security articles", href: "/blogs" },
  },
]);
